import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Cart panel: a modal dialog that slides in from the inline end at every viewport width.
 *
 * Lines, quantities and section refreshes are owned by the `<cart-items-component>` it
 * contains. This element only owns the dialog lifecycle: open, animated close, backdrop
 * click, Escape, scroll lock and opening after an add to cart.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The modal dialog.
 *
 * @extends {Component<Refs>}
 */
class CartPanelComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    const { dialog } = this.refs;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    dialog.addEventListener('cancel', this.#handleCancel);
    dialog.addEventListener('click', this.#handleClick);
    dialog.addEventListener('close', this.#handleClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    unlockScroll(this);
  }

  get isOpen() {
    return this.refs.dialog.open && !this.hasAttribute('closing');
  }

  open() {
    const { dialog } = this.refs;
    this.removeAttribute('closing');
    if (dialog.open) return;

    dialog.showModal();
    lockScroll(this);
  }

  /**
   * Plays the closing animation, then closes the dialog. Without animation (reduced
   * motion) the dialog closes at once. Opening again while closing cancels the close.
   */
  async close() {
    if (!this.isOpen) return;

    const { dialog } = this.refs;
    this.setAttribute('closing', '');
    await Promise.allSettled(dialog.getAnimations().map((animation) => animation.finished));

    if (this.hasAttribute('closing')) dialog.close();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  #handleClose = () => {
    this.removeAttribute('closing');
    unlockScroll(this);
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  /**
   * Opens the panel after an add to cart. A cart that already has lines opens with the
   * click; an empty one waits for the server so it never opens on its empty state.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (!this.hasAttribute('auto-open') || event.action !== 'add' || this.isOpen) return;

    if (!this.querySelector('[data-cart-drawer-empty]')) this.open();

    // A rejected add is reported by the form that sent it: the panel just stays as it is.
    event.promise?.then(({ detail }) => !detail?.didError && this.open()).catch(() => {});
  };
}

if (!customElements.get('cart-panel-component')) {
  customElements.define('cart-panel-component', CartPanelComponent);
}
